import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import { parseQueryString } from '@zg-rentals/string-utils';
import {
  AuthCheckWrapper,
  HeadWrapper,
  LoadingConstants,
  PermissionCheck,
  useLogger,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import { Box, Icon, Page, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconChevronRightFilled } from '@zillow/constellation-icons';

import { INVOICE_PAYMENTS } from '../constants/RouteConstants';
import AutopayStatusWidget from '../components/AutopayStatusWidget';
import BillingPaymentMethods from '../components/BillingPaymentMethods';
import BillingPortalMaintenanceBanner from '../components/BillingPortalMaintenanceBanner';
import InvoiceNotificationsManager from '../components/InvoiceNotificationsManager';
import { useAppMaintenance } from '../hooks/useMaintenance';
import { useAccountPaymentMethods } from '../hooks/useAccountPaymentMethods';
import { emitAddAutopayEvent } from '../model/ClickstreamEvents';
import { Heading } from '@zillow/constellation-10';

const PAGE_TITLE = 'Manage payment details';

const AccountPaymentPage = () => {
  const match = useRouteMatch();
  // @ts-expect-error - params type not properly defined
  const { accountNumber } = match.params;
  const paths = useRmwPaths();
  const location = useLocation();
  const queryParams = parseQueryString(location.search);
  const { isMaintenanceMode } = useAppMaintenance();
  const { saveDefaultZuoraPaymentMethod, paymentMethodState, getAccountPaymentMethods, setPaymentMethodLock } =
    useAccountPaymentMethods();
  const { loadingState, accountName } = paymentMethodState;
  const { user } = useRentalManagerUser();
  const logger = useLogger();

  const isLoaded = loadingState === LoadingConstants.SUCCESS;

  const handleEnableAutopay = (selectedCreditCardId: string | undefined | null, autopayEnabled?: boolean) => {
    if (!autopayEnabled) {
      return Promise.reject(new Error('Autopay conditions not accepted'));
    }
    if (!selectedCreditCardId) {
      logger.error('Attempting to turn on autopay without selected payment method');
      return Promise.reject();
    }
    emitAddAutopayEvent({ user });
    return saveDefaultZuoraPaymentMethod({
      accountNumber: accountNumber,
      defaultPaymentMethodId: selectedCreditCardId,
      autopayEnabled,
    }).then(() => {
      setPaymentMethodLock(false);
      return getAccountPaymentMethods(accountNumber);
    });
  };

  const getBackText = () => {
    switch (queryParams.prev) {
      case 'invoice':
        return 'Back to Invoice';
      case 'invoicePayment':
        return 'Back to Invoice Payment';
      case 'due':
        return 'Back to Invoices';
      case 'settings':
      default:
        return 'Back To Settings';
    }
  };

  const getBackLink = () => {
    switch (queryParams.prev) {
      case 'invoice':
        return `${paths.billingPage}/${paths.invoicePage}/${queryParams.invoiceNumber}`;
      case 'invoicePayment':
        return `${paths.billingPage}/${paths.invoicePage}/${queryParams.invoiceNumber}/payment`;
      case 'due':
        return `${paths.billingPage}/due`;
      case 'settings':
      default:
        return '/rental-manager/billing/paymentSettings';
    }
  };

  return (
    <AuthCheckWrapper>
      <PermissionCheck route={INVOICE_PAYMENTS}>
        <div className="AccountPaymentPage">
          <HeadWrapper title={PAGE_TITLE} />
          <Box css={{ maxWidth: '1280px', marginInline: 'auto', paddingInline: 'md' }}>
            <Page.Breadcrumb css={{ marginBottom: 'lg', paddingInline: '24px' }}>
              <TextButton asChild icon={<IconChevronLeftFilled />}>
                <Link to={getBackLink()}>{getBackText()}</Link>
              </TextButton>
            </Page.Breadcrumb>
            <BillingPortalMaintenanceBanner />
            <Box
              css={{
                background: 'white',
                marginBottom: '20px',
                border: '1px solid',
                // oxlint-disable-next-line constellation/prefer-semantic-tokens -- pre-existing base-token usage; was warn-only under ESLint's pandaLint
                borderColor: 'gray.300',
                borderRadius: '5px',
              }}
            >
              <Box css={{ padding: 'lg' }}>
                <Box>
                  <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
                    <Box css={{ width: { base: '100%', md: '50%' } }}>
                      <Box css={{ paddingBottom: 'md' }}>
                        <Text>
                          Account{' '}
                          <Icon size="sm">
                            <IconChevronRightFilled />
                          </Icon>{' '}
                          {accountName}
                        </Text>
                      </Box>
                      <Heading level={4}>{PAGE_TITLE}</Heading>
                      <Box css={{ paddingBottom: 'lg' }} />
                      <BillingPaymentMethods
                        accountNumber={accountNumber}
                        onSubmit={handleEnableAutopay}
                        allowRemove={true}
                      />
                      <AutopayStatusWidget
                        hideSetup={true}
                        disabled={!isLoaded || isMaintenanceMode}
                        accountNumber={accountNumber}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              <InvoiceNotificationsManager accountNumber={accountNumber} />
            </Box>
          </Box>
        </div>
      </PermissionCheck>
    </AuthCheckWrapper>
  );
};

export default AccountPaymentPage;
